import React, { useState, useRef, useEffect } from "react";
import FormTitle from "../../FormElements/FormTitle";
import CardInfo from "../../FormElements/CardInfo";
import { ObligationAbroadForm } from "../../../Pages/wizard/obligationAbroadForm";
import Title from "../../FormElements/Title";
import { FormContext, useForm } from "react-hook-form";
import ErrorCustom from "../../errorCustom";
import Button from "../../FormElements/Button";
import { useSelector } from "react-redux";
import { Address } from "../../../Pages/wizard/Address";
import AlertMessageSvgIcon from "../../../Components/alertMessageSvgIcon";
import IconInfoCircle from "../../../Resources/svg/Icons/IconInfoCircle";
import IconPlus from "../../../Resources/svg/Icons/IconPlus";
import {
  validateEmail,
  validatePhone,
  validatePhoneVoid,
  validateTin,
  validatorClientText,
  validatorField,
} from "../../../Utils/validators";

interface Props {
  userData: any;
  onSubmit: any;
  setFormInsuredValid: any;
  nameUbigeo: any;
  setNameUbigeo: any;
}

interface Object {
  civilStatus: any;
  profession: any;
  occupation: any;
  nationality: any;
  country: any;
  phone: any;
  usTin: any;
  mobile: any;
  email: any;
  residentUSA: any;
  obligationsAbroad: any;
  address: any;
}

const myObject: Object = {
  civilStatus: "",
  profession: "",
  occupation: "",
  nationality: "",
  country: "",
  usTin: "",
  phone: "",
  mobile: "",
  email: "",
  residentUSA: "",
  obligationsAbroad: "",
  address: "",
};

const obligationsDefaultState = {
  codigoPais: "",
  descripcionPais: "",
  obligacionTributaria: "",
};

const MAX_NUMBER_OBLIGATIONS_ABROAD = 10;

const InsuredForm = ({
  userData,
  onSubmit,
  setFormInsuredValid,
  nameUbigeo,
  setNameUbigeo,
}: Props) => {
  const methods = useForm({ mode: "onChange" });
  const { handleSubmit, register, errors, formState, watch } = methods;
  const { isValid } = formState;
  const inputEl = useRef(null);
  const showUsTIN = watch("residentUSA");
  const showObligationsAbroad = watch("obligationsAbroad");

  const [nameVia, setNameVia] = useState<any>({
    via: "",
  });
  const [obligationsAbroad, setObligationsAbroad] = useState([
    obligationsDefaultState,
  ]);
  const [obligationsErrors, setObligationsErrors] = useState([true]);
  const [obligationErrorMessage, setObligationErrorMessage] = useState("");
  const [isFormCompletelyValid, setIsFormCompletelyValid] = useState(false);

  const data = useSelector((state: any) => state.simulate.policy.client);
  
  const [dataCountry] = useSelector((state: any) => state.quiz.countryOA);
  const countriesList = dataCountry && dataCountry.childControls || [];

  let dataRedux;
  if (Object.entries(data).length !== 0) {
    dataRedux = data.datosCliente.datosParticulares;
  }

  const {
    civilStatus,
    profession: profesion,
    occupation: ocupacion,
    nationality: nacionalidad,
    country: pais,
    usTin,
    phone: telefono,
    mobile: movil,
    email,
    residentUSA: residenciadoUSA,
    obligationsAbroad: obligacionesExtranjero,
    address,
  } = dataRedux || myObject;

  useEffect(() => {
    const hasObligationErrors = obligationsErrors.some(
      (error) => error === true
    );

    const hasAtLeastOneValidObligation =
      showObligationsAbroad === "1"
        ? obligationsAbroad.some(
            (obl: any) =>
              obl.codigoPais &&
              obl.obligacionTributaria &&
              obl.obligacionTributaria.trim() !== ""
          )
        : true;

    const formValid =
      isValid &&
      (!showObligationsAbroad ||
        showObligationsAbroad === "0" ||
        (!hasObligationErrors && hasAtLeastOneValidObligation));

    setIsFormCompletelyValid(formValid);
    setFormInsuredValid(formValid);
  }, [isValid, obligationsErrors, showObligationsAbroad, obligationsAbroad]);

  useEffect(() => {
    if (obligationErrorMessage && obligationsAbroad.length > 0) {
      const lastObligation = obligationsAbroad[obligationsAbroad.length - 1];
      if (
        lastObligation.codigoPais &&
        lastObligation.obligacionTributaria &&
        lastObligation.obligacionTributaria.trim() !== ""
      ) {
        setObligationErrorMessage("");
      }
    }
  }, [obligationsAbroad, obligationErrorMessage]);

  const addItemObligationAbroad = (e: any) => {
    e.preventDefault();

    if (obligationsAbroad.length >= MAX_NUMBER_OBLIGATIONS_ABROAD) {
      return;
    }

    const lastObligation = obligationsAbroad[obligationsAbroad.length - 1];
    if (
      !lastObligation.codigoPais ||
      !lastObligation.obligacionTributaria ||
      lastObligation.obligacionTributaria.trim() === ""
    ) {
      setObligationErrorMessage(
        "* Se deben completar los datos solicitados antes de añadir otro país."
      );
      return;
    }

    setObligationErrorMessage("");
    setObligationsAbroad([...obligationsAbroad, obligationsDefaultState]);
    setObligationsErrors([...obligationsErrors, true]);
  };

  const removeObligationAbroad = (index: number) => {
    const newObligations = [...obligationsAbroad];
    newObligations.splice(index, 1);
    setObligationsAbroad(newObligations);

    const newErrors = [...obligationsErrors];
    newErrors.splice(index, 1);
    setObligationsErrors(newErrors);
  };

  const updateObligation = (value: any, index: number, hasError: boolean) => {
    const newObligations = [...obligationsAbroad];
    newObligations[index] = value;
    
    // Validar si el país ya fue seleccionado en otro registro
    if (value.codigoPais) {
      const isDuplicate = newObligations.some(
        (obl, idx) => idx !== index && obl.codigoPais === value.codigoPais
      );
      
      if (isDuplicate) {
        hasError = true;
      }
    }
    
    setObligationsAbroad(newObligations);

    const newErrors = [...obligationsErrors];
    newErrors[index] = hasError;
    setObligationsErrors(newErrors);
  };

  const handleFormSubmit = (data: any) => {
    const formData = {
      ...data,
      obligationsAbroadList:
        showObligationsAbroad === "1" ? obligationsAbroad : [],
    };
    onSubmit(formData);
  };

  return (
    <>
      {userData && (
        <>
          <FormTitle
            title="Ingresa los datos personales del asegurado"
            parrafo="El contratante es la persona que es titular de la póliza y asegurado a la vez."
          />
          <CardInfo client={userData} />
          <Title title="Otros datos personales" />
          <FormContext {...methods}>
            <form ref={inputEl} onSubmit={handleSubmit(handleFormSubmit)}>
              <div className="clientForm__control input-field">
                <select
                  className="browser-default"
                  id="civilStatus"
                  name="civilStatus"
                  ref={register({ validate: validatorField })}
                >
                  <option disabled>Estado civil</option>

                  <option
                    value="SOLTERO"
                    selected={
                      String("") === String(civilStatus) ? true : undefined
                    }
                  >
                    Soltera(o)
                  </option>
                  <option
                    value="Casada(o)"
                    selected={
                      String("Casada(o)") === String(civilStatus)
                        ? true
                        : undefined
                    }
                  >
                    Casada(o)
                  </option>
                  <option
                    value="Divorciada(o)"
                    selected={
                      String("Divorciada(o)") === String(civilStatus)
                        ? true
                        : undefined
                    }
                  >
                    Divorciada(o)
                  </option>
                  <option
                    value="Viuda(o)"
                    selected={
                      String("Viuda(o)") === String(civilStatus)
                        ? true
                        : undefined
                    }
                  >
                    Viuda(o)
                  </option>
                </select>
                <ErrorCustom errors={errors} name="civilStatus" />
              </div>

              <div className="clientForm__control input-field">
                <input
                  id="profession"
                  name="profession"
                  className={errors["profession"] ? " is-error" : ""}
                  ref={register({ validate: validatorClientText })}
                  defaultValue={profesion}
                  maxLength={50}
                  type="text"
                />
                <label
                  htmlFor="profession"
                  className={profesion ? "active" : "false"}
                >
                  Profesión
                </label>
                <ErrorCustom errors={errors} name="profession" />
              </div>

              <div className="clientForm__control input-field">
                <input
                  id="occupation"
                  name="occupation"
                  className={errors["occupation"] ? " is-error" : ""}
                  ref={register({ validate: validatorClientText })}
                  defaultValue={ocupacion}
                  maxLength={50}
                  type="text"
                />
                <label
                  htmlFor="occupation"
                  className={ocupacion ? "active" : "false"}
                >
                  Ocupación
                </label>
                <ErrorCustom errors={errors} name="occupation" />
              </div>

              <Title title="Datos migratorios" />

              <div className="group-form">
                <div className="clientForm__control input-field">
                  <input
                    id="nationality"
                    name="nationality"
                    className={errors["nationality"] ? " is-error" : ""}
                    ref={register({ validate: validatorClientText })}
                    maxLength={50}
                    defaultValue={nacionalidad}
                    type="text"
                  />
                  <label
                    htmlFor="nationality"
                    className={nacionalidad ? "active" : "false"}
                  >
                    Nacionalidad
                  </label>
                  <ErrorCustom errors={errors} name="nationality" />
                </div>

                <div className="clientForm__control input-field">
                  <input
                    id="country"
                    name="country"
                    defaultValue={pais}
                    maxLength={50}
                    type="text"
                    className={errors["country"] ? " is-error" : ""}
                    ref={register({ validate: validatorClientText })}
                  />
                  <label
                    htmlFor="country"
                    className={pais ? "active" : "false"}
                  >
                    País de residencia
                  </label>
                  <ErrorCustom errors={errors} name="country" />
                </div>
              </div>

              <p className="textGroup">¿Es ciudadano o residente de EE.UU?</p>
              <div
                className={
                  "clientForm__form__item" +
                  (errors["residentUSA"] ? " is-error" : "")
                }
              >
                <p>
                  <label>
                    <input
                      className="with-gap"
                      name="residentUSA"
                      ref={register({ validate: validatorField })}
                      id="yes_residentUSA"
                      type="radio"
                      value="1"
                      defaultChecked={residenciadoUSA === "1"}
                    />
                    <span>Sí</span>
                  </label>
                </p>
                <p>
                  <label>
                    <input
                      className="with-gap"
                      name="residentUSA"
                      ref={register({ validate: validatorField })}
                      id="no_residentUSA"
                      value="0"
                      type="radio"
                      defaultChecked={residenciadoUSA === "0"}
                    />
                    <span>No</span>
                  </label>
                </p>
                <ErrorCustom errors={errors} name="residentUSA" />
              </div>

              {showUsTIN === "1" && (
                <>
                  <div className="clientForm__control input-field is-optional">
                    <input
                      id="usTin"
                      name="usTin"
                      defaultValue={usTin || ""}
                      autoComplete="off"
                      ref={register({ validate: validateTin })}
                      maxLength={30}
                      type="text"
                    />
                    <label
                      htmlFor="usTin"
                      className={usTin ? "active" : "false"}
                    >
                      US TIN
                    </label>
                    <div>(opcional)</div>
                  </div>
                  <ErrorCustom errors={errors} name="usTin" />
                </>
              )}

              <p className="textGroup">
                ¿Tienes obligaciones tributarias fuera de Perú?
              </p>
              <div
                className={
                  "clientForm__form__item" +
                  (errors["obligationsAbroad"] ? " is-error" : "")
                }
              >
                <p>
                  <label>
                    <input
                      className="with-gap"
                      name="obligationsAbroad"
                      ref={register({ validate: validatorField })}
                      id="yes_obligationsAbroad"
                      type="radio"
                      value="1"
                      defaultChecked={obligacionesExtranjero === "1"}
                    />
                    <span>Sí</span>
                  </label>
                </p>
                <p>
                  <label>
                    <input
                      className="with-gap"
                      name="obligationsAbroad"
                      ref={register({ validate: validatorField })}
                      id="no_obligationsAbroad"
                      value="0"
                      type="radio"
                      defaultChecked={obligacionesExtranjero === "0"}
                    />
                    <span>No</span>
                  </label>
                </p>
                <ErrorCustom errors={errors} name="obligationsAbroad" />
              </div>

              {showObligationsAbroad === "1" && (
                <div
                  className={`clientForm__container`}
                  style={{ marginBottom: "32px", padding: "0px" }}
                >
                  {obligationsAbroad.map((obligation: any, index: number) => {
                    const availableCountries = countriesList.filter(
                      (country: any) =>
                        !obligationsAbroad.some(
                          (obl: any, idx: number) =>
                            idx !== index && obl.codigoPais === country.codigo
                        )
                    );
                    
                    return (
                      <ObligationAbroadForm
                        key={index}
                        obligation={obligation}
                        index={index}
                        updateObligation={updateObligation}
                        removeObligation={removeObligationAbroad}
                        count={obligationsAbroad.length}
                        countries={availableCountries}
                      />
                    );
                  })}
                  {obligationErrorMessage && (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        marginTop: "8px",
                        color: "#ff1c44",
                        fontSize: "14px",
                        fontFamily: "Lato",
                      }}
                    >
                      <span>{obligationErrorMessage}</span>
                    </div>
                  )}

                  <div
                    className={`clientForm__add ${
                      obligationsAbroad.length >= MAX_NUMBER_OBLIGATIONS_ABROAD
                        ? "disabled-action"
                        : ""
                    }`}
                    onClick={(e) => addItemObligationAbroad(e)}
                  >
                    <IconPlus
                      style={{
                        cursor:
                          obligationsAbroad.length >=
                          MAX_NUMBER_OBLIGATIONS_ABROAD
                            ? "auto"
                            : "pointer",
                      }}
                    />
                    <p className="clientForm__add-text">Añadir otro país</p>
                  </div>
                </div>
              )}

              {(showUsTIN === "1" || showObligationsAbroad === "1") && (
                <>
                  <AlertMessageSvgIcon
                    icono={
                      <IconInfoCircle
                        width={30}
                        height={30}
                        viewBox="0 0 20 20"
                        style={{ cursor: "auto" }}
                      />
                    }
                    estilosContenedor={{
                      style: { background: "#edeffc", marginTop: "16px" },
                    }}
                    estilosIcono={{
                      style: {
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      },
                    }}
                    estilosMensaje={{
                      style: { textAlign: "justify", paddingRight: "10px" },
                    }}
                    mensaje="Autorizo a Rimac a cumplir obligaciones establecidas por las autoridades competentes, en relación a la normativa FATCA y CRS."
                  />
                </>
              )}

              <Title title="Datos de contacto" />

              <div className="clientForm__control input-field is-optional">
                <input
                  id="phone"
                  name="phone"
                  defaultValue={telefono || ""}
                  autoComplete="off"
                  ref={register({ validate: validatePhoneVoid })}
                  maxLength={9}
                  type="text"
                />
                <label
                  htmlFor="phone"
                  className={telefono ? "active" : "false"}
                >
                  Teléfono de casa
                </label>
                <div>(opcional)</div>
              </div>
              <ErrorCustom errors={errors} name="phone" />

              <div className="input clientForm__control input-field">
                <input
                  id="mobile"
                  name="mobile"
                  className={errors["mobile"] ? " is-error" : ""}
                  ref={register({ validate: validatePhone })}
                  defaultValue={userData.mobile || movil}
                  maxLength={9}
                  type="text"
                />
                <label
                  htmlFor="mobile"
                  className={userData.mobile || movil ? "active" : "false"}
                >
                  Celular
                </label>
                <ErrorCustom errors={errors} name="mobile" />
              </div>

              <div className="input clientForm__control input-field">
                <input
                  id="email"
                  name="email"
                  defaultValue={email || userData.email}
                  maxLength={100}
                  type="text"
                  className={errors["email"] ? " is-error" : ""}
                  ref={register({ validate: validateEmail })}
                />
                <label
                  htmlFor="email"
                  className={userData.email || email ? "active" : "false"}
                >
                  Correo electrónico
                </label>
                <ErrorCustom errors={errors} name="email" />
              </div>

              <Title title="Domicilio" />
              <Address
                nameUbigeo={nameUbigeo}
                setNameUbigeo={setNameUbigeo}
                nameVia={nameVia}
                setNameVia={setNameVia}
              />
              <div className="form-register__border"></div>
              <div className="form-register__buttom form-step">
                <Button type="submit" disabled={!isFormCompletelyValid}>
                  Continuar
                </Button>
              </div>
            </form>
          </FormContext>
        </>
      )}
    </>
  );
};

export default InsuredForm;
